import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ShieldCheck, Lock, Bot, FileCheck, BadgeCheck, Eye, ArrowRight } from 'lucide-react';

const PILLARS = [
  {
    icon: ShieldCheck,
    title: 'Verified sellers',
    body: 'Every seller passes phone, email, and government-ID verification before they can list. Suspicious accounts are blocked automatically.',
  },
  {
    icon: Lock,
    title: 'Escrow-secured payments',
    body: 'Funds are held in a regulated escrow with Paystack until both parties confirm the handover. No more bank-transfer scams.',
  },
  {
    icon: Bot,
    title: 'AI fraud detection',
    body: 'Listing photos, prices, and descriptions are scanned by ML for stolen-image reuse, doctored plates, and abnormal pricing.',
  },
  {
    icon: FileCheck,
    title: 'VIN authentication',
    body: 'Every car listing is checked against international VIN registries for theft, accident, and ownership history.',
  },
  {
    icon: BadgeCheck,
    title: 'Dispute resolution',
    body: 'A dedicated trust & safety team mediates disputes within 48 hours. Buyers and sellers are protected by a written policy.',
  },
  {
    icon: Eye,
    title: 'Transparent pricing',
    body: 'Every listing carries an AI-generated fair-price band so buyers know if they\u2019re paying market rate before they message a seller.',
  },
];

export default function Trust() {
  return (
    <main data-testid="trust-page">
      <section className="max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-10">
        <p className="text-xs tracking-[0.18em] uppercase text-gold mb-3 font-medium">Trust & safety</p>
        <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
          The most-trusted way to<br className="hidden md:block" /> buy a car in Ghana.
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
          Six layers of protection, designed for the realities of the Ghanaian market.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12 border-t border-border/40">
        {PILLARS.map(({ icon: Icon, title, body }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.35, delay: Math.min(i * 0.05, 0.2) }}
            data-testid={`trust-pillar-${i}`}
          >
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center text-gold mb-4">
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
          </motion.div>
        ))}
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-16 border-t border-border/40">
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">Ready when you are.</h2>
            <p className="text-muted-foreground max-w-md">
              Whether you&rsquo;re buying your first vehicle or listing your tenth, AutoTrust handles the trust so you can focus on the deal.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <Link to="/browse"><Button className="bg-gold text-black hover:brightness-110 font-semibold gap-2 w-full sm:w-auto" data-testid="trust-cta-browse">Browse inventory <ArrowRight className="w-4 h-4" /></Button></Link>
            <Link to="/sell"><Button variant="outline" className="w-full sm:w-auto" data-testid="trust-cta-sell">Start a listing</Button></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
